import { useState } from 'react';

// Import all images
import reactImg from '../assets/reactjs.png';
import flutterImg from '../assets/flutter.png';
import vuejsImg from '../assets/vuejs.webp';
import csharpImg from '../assets/csharp.png';
import nodejsImg from '../assets/node.png';
import javascriptImg from '../assets/js.png';
import mongodbImg from '../assets/mongodb.webp';
import mysqlImg from '../assets/mysql.svg';

export default function ProfessionalSkills() {
  const [activeSkill, setActiveSkill] = useState(null);
  
  const skills = [
    {
      id: 'react',
      name: 'React.js',
      description: 'Frontend development with React.js, creating interactive UI components and single-page applications.',
      color: '#61DAFB',
      image: reactImg
    },
    {
      id: 'flutter',
      name: 'Flutter',
      description: 'Cross-platform mobile app development using Dart and Flutter framework.',
      color: '#0175C2',
      image: flutterImg
    },
    {
      id: 'vuejs',
      name: 'Vue.js',
      description: 'Progressive JavaScript framework for building modern user interfaces and single-page applications.',
      color: '#4FC08D',
      image: vuejsImg
    },
    {
      id: 'csharp',
      name: 'C# .NET',
      description: 'Backend development with C# and .NET framework, building robust server-side applications.',
      color: '#9B4F96',
      image: csharpImg
    },
    {
      id: 'nodejs',
      name: 'Node.js',
      description: 'Server-side JavaScript runtime environment for building scalable network applications.',
      color: '#339933',
      image: nodejsImg
    },
    {
      id: 'javascript',
      name: 'JavaScript',
      description: 'Core programming language for web development, enabling interactive and dynamic content.',
      color: '#F7DF1E',
      image: javascriptImg
    },
    {
      id: 'mongodb',
      name: 'MongoDB',
      description: 'NoSQL document database with scalability and flexibility for modern applications.',
      color: '#47A248',
      image: mongodbImg
    },
    {
      id: 'mysql',
      name: 'MySQL',
      description: 'Relational database management system for structured data storage and retrieval.',
      color: '#4479A1',
      image: mysqlImg
    }
  ];

  const styles = {
    container: {
      fontFamily: 'Roboto, sans-serif',
      maxWidth: '900px',
      margin: '0 auto',
      padding: '2rem',
      backgroundColor: '#f8f9fa',
      borderRadius: '8px',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
    },
    header: {
      textAlign: 'center',
      marginBottom: '2rem'
    },
    title: {
      fontSize: '2rem',
      color: '#333',
      marginBottom: '0.5rem'
    },
    subtitle: {
      fontSize: '1.2rem',
      color: '#666',
      fontWeight: 'normal'
    },
    skillsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
      gap: '1.5rem'
    },
    skillCard: {
      padding: '1.5rem',
      borderRadius: '8px',
      backgroundColor: 'white',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
      cursor: 'pointer',
      transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
      display: 'flex',
      flexDirection: 'column',
      gap: '1rem'
    },
    skillImageContainer: {
      height: '80px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '0.5rem'
    },
    skillImage: {
      maxHeight: '70px',
      maxWidth: '100%',
      objectFit: 'contain'
    },
    skillInfo: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.75rem'
    },
    skillName: {
      fontSize: '1.3rem',
      fontWeight: '600',
      textAlign: 'center',
      color: '#333'
    },
    skillDescription: {
      fontSize: '0.95rem',
      color: '#555',
      lineHeight: '1.5',
      textAlign: 'center'
    },
    progressBarContainer: {
      height: '12px',
      backgroundColor: '#e9ecef',
      borderRadius: '6px',
      overflow: 'hidden'
    },
    progressBar: {
      height: '100%',
      borderRadius: '6px'
    },
    modal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 100
    },
    modalContent: {
      backgroundColor: 'white',
      borderRadius: '8px',
      padding: '2rem',
      maxWidth: '600px',
      width: '90%',
      maxHeight: '80vh',
      overflow: 'auto'
    },
    modalHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
      marginBottom: '1.5rem'
    },
    modalImageContainer: {
      width: '60px',
      height: '60px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    modalTitle: {
      fontSize: '1.5rem',
      fontWeight: '600',
      color: '#333'
    },
    detailsTitle: {
      fontSize: '1.1rem',
      fontWeight: '600',
      color: '#333',
      marginBottom: '0.5rem'
    },
    detailsList: {
      listStyleType: 'none',
      padding: 0,
      margin: 0
    },
    detailsItem: {
      padding: '0.5rem 0',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem'
    },
    detailsIcon: {
      fontWeight: 'bold'
    },
    closeButton: {
      alignSelf: 'flex-end',
      border: 'none',
      borderRadius: '4px',
      padding: '0.5rem 1rem',
      cursor: 'pointer',
      fontSize: '0.9rem',
      fontWeight: '500',
      marginTop: '1.5rem'
    }
  };

  const getSkillDetails = (skillId) => {
    switch (skillId) {
      case 'react':
        return [
          'Component-based architecture',
          'State management with Redux and Context API',
          'React Router for navigation',
          'REST API integration',
          'Responsive design with styled-components',
          'React Hooks for functional components'
        ];
      case 'flutter':
        return [
          'UI development with Material Design and Cupertino',
          'State management with Provider and Bloc',
          'Firebase integration for authentication and storage',
          'RESTful API consumption',
          'Native feature access via platform channels',
          'Cross-platform deployment (iOS, Android, Web)'
        ];
      case 'vuejs':
        return [
          'Single-file component architecture',
          'Vue Router for SPA development',
          'Vuex for state management',
          'Template syntax and directives',
          'Vue CLI for project scaffolding',
          'Composition API'
        ];
      case 'csharp':
        return [
          'ASP.NET Core MVC development',
          'Entity Framework Core for ORM',
          'Web API creation with REST principles',
          'LINQ for data manipulation',
          'Unit testing with xUnit and Moq',
          'Dependency Injection patterns'
        ];
      case 'nodejs':
        return [
          'Express.js framework for web applications',
          'RESTful API development',
          'Authentication with JWT',
          'Middleware implementation',
          'Asynchronous programming with Promises',
          'Integration with various databases'
        ];
      case 'javascript':
        return [
          'ES6+ features and syntax',
          'Asynchronous programming with Promises and async/await',
          'DOM manipulation',
          'Functional programming concepts',
          'Object-oriented programming patterns',
          'Error handling and debugging'
        ];
      case 'mongodb':
        return [
          'Document schema design',
          'CRUD operations',
          'Aggregation framework',
          'Indexing for performance',
          'MongoDB Atlas cloud integration',
          'Data modeling for NoSQL'
        ];
      case 'mysql':
        return [
          'Database schema design',
          'SQL query optimization',
          'Stored procedures and triggers',
          'Transaction management',
          'Indexing strategies',
          'Database normalization'
        ];
      default:
        return [];
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Technical Skills Portfolio</h1>
        <h2 style={styles.subtitle}>Full Stack Developer specializing in modern frameworks and technologies</h2>
      </div>
      
      <div style={styles.skillsGrid}>
        {skills.map(skill => (
          <div 
            key={skill.id}
            style={{
              ...styles.skillCard,
              transform: activeSkill === skill.id ? 'translateY(-3px)' : 'none',
              boxShadow: activeSkill === skill.id ? '0 6px 12px rgba(0, 0, 0, 0.1)' : styles.skillCard.boxShadow,
              borderTop: `4px solid ${skill.color}`
            }}
            onClick={() => setActiveSkill(activeSkill === skill.id ? null : skill.id)}
          >
            <div style={styles.skillImageContainer}>
              <img
                src={skill.image}
                alt={`${skill.name} logo`}
                style={styles.skillImage}
              />
            </div>
            <div style={styles.skillInfo}>
              <div style={styles.skillName}>{skill.name}</div>
              <div style={styles.skillDescription}>{skill.description}</div>
              <div style={styles.progressBarContainer}>
                <div 
                  style={{
                    ...styles.progressBar,
                    width: '100%',
                    backgroundColor: skill.color
                  }}
                />
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {activeSkill && (
        <div style={styles.modal} onClick={() => setActiveSkill(null)}>
          <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div style={styles.modalImageContainer}>
                <img
                  src={skills.find(s => s.id === activeSkill).image}
                  alt={`${skills.find(s => s.id === activeSkill).name} logo`}
                  style={styles.skillImage}
                />
              </div>
              <div style={styles.modalTitle}>{skills.find(s => s.id === activeSkill).name}</div>
            </div>
            
            <div style={styles.detailsTitle}>Professional Experience:</div>
            <ul style={styles.detailsList}>
              {getSkillDetails(activeSkill).map((detail, index) => (
                <li key={index} style={styles.detailsItem}>
                  <span style={{...styles.detailsIcon, color: skills.find(s => s.id === activeSkill).color}}>✓</span>
                  {detail}
                </li>
              ))}
            </ul>
            
            <button 
              style={{
                ...styles.closeButton,
                backgroundColor: skills.find(s => s.id === activeSkill).color,
                color: '#fff'
              }}
              onClick={() => setActiveSkill(null)}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}